import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import BaseTable from "../../components/base/BaseTable";
import ReportDetailModal from "../../components/report/ReportDetailModal";
import ReportFilter from "../../components/report/ReportFilter";
import UpdateCategoryModal from "../../components/report/UpdateCategoryModal";
import AssignStaffModal from "../../components/report/AssignStaffModal";
import ReportCreateModal from "../../components/report/ReportCreateModal";
import ReportExtensionModal from "../../components/report/ReportExtensionModal";
import ReportApproveModal from "../../components/report/ReportApproveModal";
import ReportRejectModal from "../../components/report/ReportRejectModal";
import PermissionGuard from "../../components/PermissionGuard";
import { useReports } from "../../hooks/useReports";
import { useReportAreas } from "../../hooks/useReportAreas";
import { usePermission } from "../../hooks/usePermission";
import { showToast } from "../../utils/toastNotification";
import {
  renderStatusBadge,
  renderCategoryBadge,
  renderUrgencyBadge,
  renderContactInfo,
  renderSlaBadge,
  getEffectiveDeadline,
  getTimeRemaining,
  computeSlaStatus,
} from "../../utils/badgeUtils";
import {
  RotateCcw,
  Plus,
  Layers,
  Inbox,
  Clock,
  CheckCircle2,
  Archive,
  AlertTriangle,
  MessageSquare,
  Settings,
  Download,
} from "lucide-react";
import ROUTE_PATH from "../../constants/routes";
import { ExtensionService } from "../../services/extensionService";
import { REPORT_API } from "../../apis/report";


const LOCKED_STATUSES = ["DA_GIAI_QUYET", "DONG"];

const ALL_REPORT_COLUMNS = [
  { key: "index", title: "STT" },
  { key: "ma_phan_anh", title: "Mã PA" },
  { key: "tieu_de", title: "Tiêu đề" },
  { key: "khu_pho", title: "Khu phố" },
  { key: "linh_vuc_phan_anh", title: "Lĩnh vực" },
  { key: "muc_do", title: "Mức độ" },
  { key: "lich_su_trang_thai", title: "Trạng thái" },
  { key: "thoi_gian_tao", title: "Ngày gửi" },
  { key: "han_xu_ly", title: "Hạn xử lý" },
  { key: "thong_tin_lien_he", title: "Thông tin liên hệ" },
  { key: "sla_status", title: "TÌNH TRẠNG" },
];

const exportClientSideExcel = (reportsData, visibleColKeys, allColsDefinition) => {
  const visibleCols = allColsDefinition.filter((c) => visibleColKeys.includes(c.key));
  const headers = visibleCols.map((c) => c.title);

  const rows = (reportsData || []).map((item, index) => {
    return visibleCols.map((col) => {
      switch (col.key) {
        case "index":
          return index + 1;
        case "ma_phan_anh":
          return item.ma_phan_anh || "";
        case "tieu_de":
          return item.tieu_de || "";
        case "khu_pho":
          return item.khu_pho || "";
        case "linh_vuc_phan_anh":
          return item.linh_vuc_phan_anh?.ten || item.linh_vuc || "";
        case "muc_do":
          return item.muc_do || "";
        case "lich_su_trang_thai":
          return (
            Array.isArray(item.lich_su_trang_thai) && item.lich_su_trang_thai[0]?.ten
              ? item.lich_su_trang_thai[0].ten
              : item.trang_thai_hien_tai || ""
          );
        case "thoi_gian_tao":
          return item.thoi_gian_tao ? dayjs(item.thoi_gian_tao).format("HH:mm DD/MM/YYYY") : "";
        case "han_xu_ly": {
          const dl = getEffectiveDeadline(item);
          return dl ? dayjs(dl).format("HH:mm DD/MM/YYYY") : "-";
        }
        case "thong_tin_lien_he":
          return `${item.ten_nguoi_phan_anh || "Ẩn danh"}${item.sdt_nguoi_phan_anh ? ` - ${item.sdt_nguoi_phan_anh}` : ""}`;
        case "sla_status": {
          const st = computeSlaStatus ? computeSlaStatus(item) : "";
          const mapLabel = {
            ON_TIME: "Còn hạn",
            NEAR_DUE: "Sắp đến hạn",
            OVERDUE: "Quá hạn",
            REJECTED_EXTENSION: "Từ chối gia hạn",
            COMPLETED_ON_TIME: "Đúng hạn",
            COMPLETED_LATE: "Trễ hạn",
            PENDING_EXTENSION: "Chờ gia hạn",
            EXTENDED: "Đã gia hạn",
          };
          return mapLabel[st] || st;
        }
        default:
          return item[col.key] || "";
      }
    });
  });

  const csvContent =
    "\uFEFF" +
    [headers.join(","), ...rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const nowStr = dayjs().format("YYYY-MM-DD");
  link.download = `danh_sach_phan_anh_${nowStr}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

const getLatestStatusCode = (report) => {
  const history = report?.lich_su_trang_thai;
  if (!Array.isArray(history) || history.length === 0) return null;
  const latest = history[history.length - 1];
  return latest?.trang_thai || latest?.trangThai || null;
};

export default function ReportList() {
  const {
    reports,
    loading,
    pagination,
    loadReports,
    loadReportById,
    loadExtent,
    loadStatusReport,
    extent,
    statusReport,
    clearError,
    updateLinhVuc,
    loadAssignableUsers,
    assignReport,
    createReport,
  } = useReports({ autoFetch: false });

  const { reportAreas, loadReportAreas } = useReportAreas({ autoFetch: false });
  const { canUpdate, can } = usePermission();

  // Lấy thông tin user từ Redux để lọc lĩnh vực theo phạm vi quyền
  const authUser = useSelector((state) => state.auth.user);
  const userPermissions = authUser?.permissions || [];
  const isFullAccess = userPermissions.includes('PA_THUONG_TRUC');
  const userCate = useMemo(() => {
    if (!authUser?.cate) return [];
    return authUser.cate.split(',').map(s => s.trim()).filter(Boolean);
  }, [authUser?.cate]);

  // Chỉ hiển thị lĩnh vực user có quyền quản lý
  const scopedReportAreas = useMemo(() => {
    if (isFullAccess || userCate.length === 0) return reportAreas;
    return (reportAreas || []).filter(area => userCate.includes(String(area.id)));
  }, [reportAreas, isFullAccess, userCate]);

  const [modalMode, setModalMode] = useState("view");

  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedReport, setSelectedReport] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const [categoryModalReport, setCategoryModalReport] = useState(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [updatingCategory, setUpdatingCategory] = useState(false);

  const [assignModalReport, setAssignModalReport] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assigningStaff, setAssigningStaff] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loadingStaffOptions, setLoadingStaffOptions] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [creatingReport, setCreatingReport] = useState(false);

  const [extensionModalReport, setExtensionModalReport] = useState(null);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [extensionPendingCount, setExtensionPendingCount] = useState(0);

  const [approveModalReport, setApproveModalReport] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

  const [rejectModalReport, setRejectModalReport] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("report_list_visible_columns");
      if (saved) return JSON.parse(saved);
    } catch (e) { }
    return ALL_REPORT_COLUMNS.map((c) => c.key);
  });
  const [showColumnSetting, setShowColumnSetting] = useState(false);
  const columnSettingRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnSettingRef.current && !columnSettingRef.current.contains(event.target)) {
        setShowColumnSetting(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleColumn = (colKey) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(colKey)
        ? prev.filter((k) => k !== colKey)
        : [...prev, colKey];
      try {
        localStorage.setItem("report_list_visible_columns", JSON.stringify(next));
      } catch (e) { }
      return next;
    });
  };

  const handleResetColumns = () => {
    const all = ALL_REPORT_COLUMNS.map((c) => c.key);
    setVisibleColumns(all);
    try {
      localStorage.setItem("report_list_visible_columns", JSON.stringify(all));
    } catch (e) { }
  };

  const [filters, setFilters] = useState(() => ({
    trangThai: searchParams.get("trangThai") || "",
    tinhTrang: searchParams.get("tinhTrang") || searchParams.get("slaStatus") || "",
    idLinhVucPhanAnh: searchParams.get("idLinhVucPhanAnh") || "",
    khuPho: searchParams.get("khuPho") || "",
    mucDo: searchParams.get("mucDo") || "",
    maPhanAnh: searchParams.get("maPhanAnh") || "",
    kyBaoCao: searchParams.get("kyBaoCao") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
    sortTime: searchParams.get("sortTime") || "desc",
    sortBy: "thoi_gian_tao",
    sortOrder: "desc",
    pageSize: 10,
  }));

  useEffect(() => {
    const paramMucDo = searchParams.get("mucDo");
    const paramTrangThai = searchParams.get("trangThai");
    const paramTinhTrang = searchParams.get("tinhTrang") || searchParams.get("slaStatus");
    const paramKyBaoCao = searchParams.get("kyBaoCao");
    const paramStartDate = searchParams.get("startDate");
    const paramEndDate = searchParams.get("endDate");

    setFilters((prev) => {
      let updated = false;
      const next = { ...prev };
      if (paramMucDo !== null && paramMucDo !== prev.mucDo) {
        next.mucDo = paramMucDo;
        updated = true;
      }
      if (paramTrangThai !== null && paramTrangThai !== prev.trangThai) {
        next.trangThai = paramTrangThai;
        updated = true;
      }
      if (paramTinhTrang !== null && paramTinhTrang !== prev.tinhTrang) {
        next.tinhTrang = paramTinhTrang;
        updated = true;
      }
      if (paramKyBaoCao !== null && paramKyBaoCao !== prev.kyBaoCao) {
        next.kyBaoCao = paramKyBaoCao;
        updated = true;
      }
      if (paramStartDate !== null && paramStartDate !== prev.startDate) {
        next.startDate = paramStartDate;
        updated = true;
      }
      if (paramEndDate !== null && paramEndDate !== prev.endDate) {
        next.endDate = paramEndDate;
        updated = true;
      }
      return updated ? next : prev;
    });
  }, [searchParams]);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const unsubscribe = ExtensionService.subscribe((data) => {
      const pending = (data || []).filter((e) => e.status === "PENDING" || e.trang_thai === "PENDING").length;
      setExtensionPendingCount(pending);
    });
    ExtensionService.getStats()
      .then((data) => {
        if (data && typeof data.pending === 'number') {
          setExtensionPendingCount(data.pending);
        }
      })
      .catch(() => {});
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          loadExtent(),
          loadStatusReport(),
          loadReportAreas({ page: 1, size: 10000, isActive: true }),
        ]);
      } catch (error) {
        showToast.error("Lỗi khi tải dữ liệu khởi tạo");
      }
    };

    loadInitialData();
    // Chỉ chạy 1 lần khi mount để tải dữ liệu khởi tạo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      fetchReportById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReportById = async (reportId) => {
    try {
      const reportData = await loadReportById(reportId);
      setSelectedReport(reportData);
      setModalMode(canUpdate ? "edit" : "view");
      setIsPreviewModalOpen(true);
    } catch {
      showToast.error("Lỗi khi tải chi tiết phản ánh");
      navigate('/reports');
    }
  }

  useEffect(() => {
    const params = {
      page: currentPage,
      size: filters.pageSize,
      trangThai: filters.trangThai,
      tinhTrang: filters.tinhTrang,
      slaStatus: filters.tinhTrang,
      idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
      khuPho: filters.khuPho,
      mucDo: filters.mucDo,
      maPhanAnh: filters.maPhanAnh,
      startDate: filters.startDate,
      endDate: filters.endDate,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      sortTime: filters.sortTime,
    };

    loadReports(params).catch((e) => {
      showToast.error(e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    filters.pageSize,
    filters.trangThai,
    filters.tinhTrang,
    filters.idLinhVucPhanAnh,
    filters.khuPho,
    filters.mucDo,
    filters.maPhanAnh,
    filters.startDate,
    filters.endDate,
    filters.sortBy,
    filters.sortOrder,
    filters.sortTime,
  ]);

  const handleFilterChange = (newFilters) => {
    // Panel "Sắp xếp theo thời gian" -> map sang sortBy/sortOrder; merge để giữ sort hiện tại
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      sortBy: "thoi_gian_tao",
      sortOrder: newFilters.sortTime || prev.sortOrder || "desc",
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      trangThai: "",
      tinhTrang: "",
      idLinhVucPhanAnh: "",
      khuPho: "",
      mucDo: "",
      maPhanAnh: "",
      kyBaoCao: "",
      startDate: "",
      endDate: "",
      sortTime: "desc",
      sortBy: "thoi_gian_tao",
      sortOrder: "desc",
      pageSize: 10,
    });
    setCurrentPage(1);
  };

  const handleSort = (key, order) => {
    const sortBy = order ? key : "thoi_gian_tao";
    const sortOrder = order || "desc";
    setFilters((f) => ({
      ...f,
      sortBy,
      sortOrder,
      // Đồng bộ select thời gian ở panel khi sort theo cột Ngày gửi
      sortTime: sortBy === "thoi_gian_tao" ? sortOrder : f.sortTime,
    }));
    setCurrentPage(1);
  };

  const handleView = async (item) => {
    try {
      setModalMode("view");
      setIsPreviewModalOpen(true);
      const reportData = await loadReportById(item.id);
      setSelectedReport(reportData);
    } catch (error) {
      showToast.error("Lỗi khi tải chi tiết phản ánh");
      setIsPreviewModalOpen(false);
    }
  };

  const handleEdit = async (item) => {
    try {
      setModalMode("edit");
      setIsPreviewModalOpen(true);
      const reportData = await loadReportById(item.id);
      setSelectedReport(reportData);
    } catch (error) {
      showToast.error("Lỗi khi tải chi tiết phản ánh");
      setIsPreviewModalOpen(false);
    }
  };

  const handleOpenApproveModal = (item) => {
    setApproveModalReport(item);
    setIsApproveModalOpen(true);
  };

  const handleCloseApproveModal = () => {
    setIsApproveModalOpen(false);
    setApproveModalReport(null);
  };

  const handleOpenRejectModal = (item) => {
    setRejectModalReport(item);
    setIsRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectModalReport(null);
  };

  const isReportPending = (r) => {
    const st = r?.lich_su_trang_thai?.[0]?.ten || r?.trang_thai || getLatestStatusCode(r) || "";
    const code = (st || "").toString().trim().toUpperCase();
    return code === "DA_GUI" || code === "ĐÃ GỬI" || st === "Đã gửi";
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOpenCategoryModal = (item) => {
    setCategoryModalReport(item);
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    if (updatingCategory) return;
    setIsCategoryModalOpen(false);
    setCategoryModalReport(null);
  };

  const handleSubmitUpdateCategory = async (idLinhVucPhanAnh, lyDo) => {
    if (!categoryModalReport?.id) return;
    try {
      setUpdatingCategory(true);
      await updateLinhVuc(categoryModalReport.id, idLinhVucPhanAnh, lyDo);
      showToast.success("Cập nhật lĩnh vực phản ánh thành công");
      setIsCategoryModalOpen(false);
      setCategoryModalReport(null);
      loadReports({
        page: currentPage,
        size: filters.pageSize,
        trangThai: filters.trangThai,
        idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
        mucDo: filters.mucDo,
        maPhanAnh: filters.maPhanAnh,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
    } catch (err) {
      showToast.error(
        typeof err === "string"
          ? err
          : err?.message || "Cập nhật lĩnh vực phản ánh thất bại"
      );
    } finally {
      setUpdatingCategory(false);
    }
  };

  const canUpdateCategoryRow = (item) => {
    const latestStatus = getLatestStatusCode(item);
    return !LOCKED_STATUSES.includes(latestStatus);
  };

  const handleOpenAssignModal = async (item) => {
    setAssignModalReport(item);
    setIsAssignModalOpen(true);
    setStaffOptions([]);
    setLoadingStaffOptions(true);
    try {
      const users = await loadAssignableUsers(item.id);
      setStaffOptions(Array.isArray(users) ? users : []);
    } catch (err) {
      showToast.error(
        typeof err === "string"
          ? err
          : err?.message || "Lấy danh sách chuyên viên xử lý thất bại"
      );
    } finally {
      setLoadingStaffOptions(false);
    }
  };

  const handleCloseAssignModal = () => {
    if (assigningStaff) return;
    setIsAssignModalOpen(false);
    setAssignModalReport(null);
    setStaffOptions([]);
  };

  const handleSubmitAssign = async (idNguoiXuLy, lyDo) => {
    if (!assignModalReport?.id) return;
    try {
      setAssigningStaff(true);
      await assignReport(assignModalReport.id, idNguoiXuLy, lyDo);
      showToast.success("Phân công xử lý phản ánh thành công");
      setIsAssignModalOpen(false);
      setAssignModalReport(null);
      setStaffOptions([]);
      loadReports({
        page: currentPage,
        size: filters.pageSize,
        trangThai: filters.trangThai,
        idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
        mucDo: filters.mucDo,
        maPhanAnh: filters.maPhanAnh,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
    } catch (err) {
      showToast.error(
        typeof err === "string"
          ? err
          : err?.message || "Phân công xử lý phản ánh thất bại"
      );
    } finally {
      setAssigningStaff(false);
    }
  };

  const canAssignRow = (item) => {
    if (!can("PA_ASSIGN")) return false;
    const latestStatus = getLatestStatusCode(item);
    const statusUpper = (latestStatus || "").toString().trim().toUpperCase();
    return statusUpper === "DANG_XU_LY" || statusUpper === "ĐANG XỬ LÝ";
  };

  const canExtensionRow = (item) => {
    if (!item) return false;

    // 1. Kiểm tra quyền tạo gia hạn (hoặc quyền xử lý/cập nhật phản ánh)
    const hasPermission = can("PA_EXTENSION_CREATE") || can("PA_UPDATE") || can("PA_CREATE");
    if (!hasPermission) return false;

    // 2. Lấy trạng thái hiện tại/mới nhất của phản ánh
    const latestStatusCode = getLatestStatusCode(item) || item?.trang_thai || item?.trangThai || "";
    const statusUpper = (latestStatusCode || "").toString().trim().toUpperCase();

    // 3. Đơn đã kết thúc/hoàn thành thì không thể xin gia hạn
    if (
      statusUpper === "DA_GIAI_QUYET" ||
      statusUpper === "ĐÃ GIẢI QUYẾT" ||
      statusUpper === "DONG" ||
      statusUpper === "ĐÓNG" ||
      statusUpper === "TU_CHOI" ||
      statusUpper === "TỪ CHỐI"
    ) {
      return false;
    }

    // 4. Chỉ cho phép gửi xin gia hạn đối với những đơn đang trong tình trạng QUÁ HẠN (OVERDUE)
    const slaStatus = computeSlaStatus(item);
    return slaStatus === "OVERDUE";
  };

  const handleCloseCreateModal = () => {
    if (creatingReport) return;
    setIsCreateModalOpen(false);
  };

  const handleSubmitCreateReport = async (data) => {
    try {
      setCreatingReport(true);
      await createReport(data);
      showToast.success("Tạo phản ánh thành công");
      setIsCreateModalOpen(false);
      // Reload current page or go back to page 1
      loadReports({
        page: 1,
        size: filters.pageSize,
        trangThai: filters.trangThai,
        idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
        mucDo: filters.mucDo,
        maPhanAnh: filters.maPhanAnh,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
      setCurrentPage(1);
    } catch (err) {
      showToast.error(
        typeof err === "string"
          ? err
          : err?.message || "Tạo phản ánh thất bại"
      );
    } finally {
      setCreatingReport(false);
    }
  };

  const handleOpenExtensionModal = (report) => {
    setExtensionModalReport(report);
    setIsExtensionModalOpen(true);
  };

  const handleCloseExtensionModal = () => {
    setExtensionModalReport(null);
    setIsExtensionModalOpen(false);
  };

  const handleRefresh = () => {
    loadReports({
      page: currentPage,
      size: filters.pageSize,
      trangThai: filters.trangThai,
      idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
      mucDo: filters.mucDo,
      maPhanAnh: filters.maPhanAnh,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    })
      .then(() => {
        showToast.success("Đã làm mới danh sách phản ánh");
      })
      .catch((e) => {
        showToast.error(e || "Lỗi khi làm mới danh sách");
      });
  };

  // Stat Counts calculation for the 6 KPI Cards (ưu tiên stats toàn bộ hệ thống trả về từ API)
  const statCounts = useMemo(() => {
    if (pagination?.stats) {
      return {
        total: pagination.stats.total ?? pagination.totalItems ?? 0,
        submitted: pagination.stats.submitted ?? pagination.stats.daGui ?? 0,
        processing: pagination.stats.processing ?? 0,
        resolved: pagination.stats.resolved ?? 0,
        closed: pagination.stats.closed ?? 0,
        overdue: pagination.stats.overdue ?? 0,
      };
    }

    const total = pagination?.totalItems || reports?.length || 0;
    let submitted = 0;
    let processing = 0;
    let resolved = 0;
    let closed = 0;
    let overdue = 0;
    const now = new Date();

    (reports || []).forEach((r) => {
      const status = r.lich_su_trang_thai?.[0]?.ten || r.trang_thai || "";
      const deadline = getEffectiveDeadline(r);
      const isUrgent = (r.muc_do || "").toLowerCase().includes("khẩn");

      if (["Đóng", "DONG"].includes(status)) {
        closed += 1;
      } else if (["Đã giải quyết", "DA_GIAI_QUYET"].includes(status)) {
        resolved += 1;
      } else if (["Đã gửi", "DA_GUI"].includes(status) || !status) {
        submitted += 1;
      } else if (["Đang xử lý", "DANG_XU_LY"].includes(status)) {
        processing += 1;
      } else if (!["Từ chối", "TU_CHOI"].includes(status)) {
        processing += 1;
      }

      if (deadline) {
        const dl = new Date(deadline);
        if (dl - now < 0) overdue += 1;
      } else if (isUrgent) {
        overdue += 1;
      }
    });

    return { total, submitted, processing, resolved, closed, overdue };
  }, [pagination?.stats, pagination?.totalItems, reports]);

  const columns = [
    {
      title: "STT",
      dataIndex: "index",
      key: "index",
      width: "60px",
      render: (value, record, index) => (
        <span className="text-xs font-semibold text-gray-700">
          #
          {((pagination?.currentPage || 1) - 1) *
            (pagination?.pageSize || filters.pageSize) +
            index +
            1}
        </span>
      ),
    },
    {
      title: "Mã PA",
      dataIndex: "ma_phan_anh",
      key: "ma_phan_anh",
      width: "110px",
      sortable: true,
      sortKey: "ma_phan_anh",
      render: (value, record) => (
        <span
          onClick={() => handleView(record)}
          className="inline-flex px-2 py-0.5 text-xs font-mono font-bold bg-gray-100 text-gray-800 border border-gray-200 rounded cursor-pointer hover:text-blue-600 transition-colors"
        >
          {value}
        </span>
      ),
    },
    {
      title: "Tiêu đề",
      key: "tieu_de",
      width: "280px",
      render: (_, record) => (
        <div className="py-0.5 max-w-[280px]">
          <div
            onClick={() => handleView(record)}
            className="text-xs md:text-sm font-semibold text-gray-900 hover:text-blue-600 cursor-pointer truncate transition-colors leading-snug"
            title={record.tieu_de}
          >
            {record.tieu_de || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Khu phố",
      dataIndex: "khu_pho",
      key: "khu_pho",
      width: "95px",
      render: (value) => (
        <span className="text-xs font-semibold text-gray-800">
          {value || "KP 01"}
        </span>
      ),
    },
    {
      title: "Lĩnh vực",
      dataIndex: "linh_vuc_phan_anh",
      key: "linh_vuc_phan_anh",
      width: "105px",
      render: (value) => renderCategoryBadge(value),
    },
    {
      title: "Mức độ",
      dataIndex: "muc_do",
      key: "muc_do",
      width: "125px",
      sortable: true,
      sortKey: "muc_do",
      render: (value) => renderUrgencyBadge(value),
    },
    {
      title: "Trạng thái",
      dataIndex: "lich_su_trang_thai",
      key: "lich_su_trang_thai",
      width: "130px",
      sortable: true,
      sortKey: "trang_thai",
      render: (value) => renderStatusBadge(value),
    },
    {
      title: "Ngày gửi",
      dataIndex: "thoi_gian_tao",
      key: "thoi_gian_tao",
      width: "120px",
      sortable: true,
      sortKey: "thoi_gian_tao",
      render: (value) => {
        if (!value) return <span className="text-xs text-gray-400 font-medium">-</span>;
        const d = dayjs(value);
        return (
          <div className="flex flex-col text-xs leading-tight">
            <span className="text-gray-800">{d.format("HH:mm")}</span>
            <span className="text-gray-500 text-[11px]">{d.format("DD/MM/YYYY")}</span>
          </div>
        );
      },
    },
    {
      title: "Hạn xử lý",
      key: "han_xu_ly",
      width: "120px",
      render: (_, record) => {
        const deadline = getEffectiveDeadline(record);
        if (!deadline) {
          return <span className="text-xs text-gray-400 font-medium">-</span>;
        }
        const d = dayjs(deadline);
        return (
          <div className="flex flex-col text-xs leading-tight">
            <span className="text-gray-800">{d.format("HH:mm")}</span>
            <span className="text-gray-500 text-[11px]">{d.format("DD/MM/YYYY")}</span>
          </div>
        );
      },
    },
    {
      title: "Thông tin liên hệ",
      key: "thong_tin_lien_he",
      width: "160px",
      render: (_, record) => {
        const name = record.ten_nguoi_phan_anh || "Ẩn danh";
        const phone = record.sdt_nguoi_phan_anh;
        return renderContactInfo({ name, phone }, 160);
      },
    },
    {
      title: "TÌNH TRẠNG",
      key: "sla_status",
      width: "155px",
      render: (_, record) => renderSlaBadge(record),
    },
  ];

  const filteredColumns = useMemo(() => {
    return columns.filter((col) => !col.key || visibleColumns.includes(col.key));
  }, [columns, visibleColumns]);

  const [isExporting, setIsExporting] = useState(false);

  const handleExportExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const selectedColKeys = visibleColumns.filter((k) => k !== "actions");
      const exportParams = {
        columns: selectedColKeys.length > 0 ? selectedColKeys : ALL_REPORT_COLUMNS.map((c) => c.key),
        search: filters.maPhanAnh || undefined,
        trangThai: filters.trangThai || undefined,
        slaStatus: filters.tinhTrang || undefined,
        idLinhVucPhanAnh: filters.idLinhVucPhanAnh || undefined,
        mucDo: filters.mucDo || undefined,
        khuPho: filters.khuPho || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortTime: filters.sortTime || "desc",
      };
      const result = await REPORT_API.exportReportExcel(exportParams);
      if (result.success && result.data) {
        const filename = `danh_sach_phan_anh_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
        const blobUrl = window.URL.createObjectURL(
          new Blob([result.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        showToast.success("Xuất file Excel thành công!");
      } else {
        showToast.error(result.error || "Xuất file Excel thất bại!");
      }
    } catch (err) {
      console.error("Lỗi xuất Excel:", err);
      showToast.error("Có lỗi xảy ra khi xuất file Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const columnSettingComponent = (
    <div className="flex items-center gap-2">
      {/* Nút Xuất Excel */}
      <button
        type="button"
        onClick={handleExportExcel}
        disabled={isExporting}
        className="px-3 py-2 bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer disabled:cursor-not-allowed"
        title="Xuất danh sách phản ánh ra file Excel"
      >
        {isExporting ? (
          <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
        ) : (
          <Download className="w-4 h-4 text-gray-500" />
        )}
        <span className="hidden sm:inline">Xuất Excel</span>
      </button>

      {/* Nút Tùy chỉnh cột hiển thị */}
      <div className="relative flex-shrink-0" ref={columnSettingRef}>
        <button
          type="button"
          onClick={() => setShowColumnSetting((prev) => !prev)}
          className={`p-2 border rounded-lg transition-all flex items-center justify-center cursor-pointer ${showColumnSetting
              ? "bg-blue-50 border-blue-400 text-blue-600 shadow-xs"
              : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`}
          title="Tùy chỉnh cột hiển thị"
        >
          <Settings className={`w-5 h-5 ${showColumnSetting ? "text-blue-600" : "text-gray-600"}`} />
        </button>

        {showColumnSetting && (
          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-3 z-50">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Cột hiển thị ({visibleColumns.length}/{ALL_REPORT_COLUMNS.length})
              </span>
              <button
                type="button"
                onClick={handleResetColumns}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline cursor-pointer"
              >
                Mặc định
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-1 py-1">
              {ALL_REPORT_COLUMNS.map((col) => {
                const isChecked = visibleColumns.includes(col.key);
                return (
                  <label
                    key={col.key}
                    className={`flex items-center justify-between px-2 py-1.5 rounded-lg text-sm transition-colors select-none cursor-pointer ${isChecked ? "hover:bg-blue-50/60 text-gray-800" : "hover:bg-gray-50 text-gray-500"
                      }`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleColumn(col.key)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="font-normal truncate">{col.title}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-5 min-h-full pb-8">
      {/* 1. HEADER TOPBAR & NÚT THAO TÁC */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 m-0">Danh sách phản ánh</h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1 mb-0">
            Quản lý, phân công và theo dõi tiến độ xử lý phản ánh của người dân
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={handleRefresh}
            className="h-9 px-3.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-xs md:text-sm font-semibold text-gray-700 shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
          >
            <RotateCcw className="w-4 h-4 text-gray-500" />
            <span>Làm mới</span>
          </button>

          <PermissionGuard permission="PA_CREATE">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="h-9 px-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs md:text-sm font-semibold shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Tạo phản ánh mới</span>
            </button>
          </PermissionGuard>
        </div>
      </div>

      {/* 3. KHỐI 6 THẺ KPI STAT CARDS */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-3.5">
        {/* Card 1: Tổng số phản ánh */}
        <div
          onClick={() => {
            const next = { ...filters, trangThai: "", tinhTrang: "" };
            handleFilterChange(next);
          }}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${!filters.trangThai && !filters.tinhTrang
              ? "border-blue-500 ring-1 ring-blue-500/30 shadow-xs bg-blue-50/20"
              : "border-gray-200 hover:border-gray-300"
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Tổng số phản ánh</span>
              <span className="text-2xl font-bold text-blue-600 tracking-tight">{statCounts.total}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 2: Đã gửi */}
        <div
          onClick={() => {
            let next;
            if (filters.trangThai === "Đã gửi" || filters.trangThai === "DA_GUI") {
              next = { ...filters, trangThai: "", tinhTrang: "" };
            } else {
              next = { ...filters, trangThai: "Đã gửi", tinhTrang: "" };
            }
            handleFilterChange(next);
          }}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            filters.trangThai === "Đã gửi" || filters.trangThai === "DA_GUI"
              ? "border-indigo-500 ring-1 ring-indigo-500/30 shadow-xs bg-indigo-50/20"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đã gửi</span>
              <span className="text-2xl font-bold text-indigo-600 tracking-tight">{statCounts.submitted}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Inbox className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 3: Đang xử lý */}
        <div
          onClick={() => {
            let next;
            if (filters.trangThai === "Đang xử lý") {
              // Bỏ chọn "Đang xử lý": nếu "Quá hạn" đang bật thì giữ "Quá hạn", ngược lại quay về Tất cả
              next = {
                ...filters,
                trangThai: "",
                tinhTrang: filters.tinhTrang === "OVERDUE" ? "OVERDUE" : "",
              };
            } else {
              // Bật "Đang xử lý": CHỈ được chọn cùng "Quá hạn" (nếu Quá hạn đang bật), các tab khác như "Đã giải quyết" bị bỏ
              next = {
                ...filters,
                trangThai: "Đang xử lý",
                tinhTrang: filters.tinhTrang === "OVERDUE" ? "OVERDUE" : "",
              };
            }
            handleFilterChange(next);
          }}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${filters.trangThai === "Đang xử lý"
              ? "border-orange-500 ring-1 ring-orange-500/30 shadow-xs bg-orange-50/20"
              : "border-gray-200 hover:border-gray-300"
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đang xử lý</span>
              <span className="text-2xl font-bold text-orange-600 tracking-tight">{statCounts.processing}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 4: Đã giải quyết */}
        <div
          onClick={() => {
            let next;
            if (filters.trangThai === "Đã giải quyết" || filters.trangThai === "DA_GIAI_QUYET") {
              next = { ...filters, trangThai: "", tinhTrang: "" };
            } else {
              next = { ...filters, trangThai: "Đã giải quyết", tinhTrang: "" };
            }
            handleFilterChange(next);
          }}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${filters.trangThai === "Đã giải quyết" || filters.trangThai === "DA_GIAI_QUYET"
              ? "border-emerald-500 ring-1 ring-emerald-500/30 shadow-xs bg-emerald-50/20"
              : "border-gray-200 hover:border-gray-300"
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đã giải quyết</span>
              <span className="text-2xl font-bold text-emerald-600 tracking-tight">{statCounts.resolved}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 5: Quá hạn / Cần gia hạn */}
        <div
          onClick={() => {
            let next;
            if (filters.tinhTrang === "OVERDUE") {
              // Bỏ chọn "Quá hạn": nếu "Đang xử lý" đang bật thì giữ "Đang xử lý", ngược lại về Tất cả
              next = {
                ...filters,
                tinhTrang: "",
                trangThai: filters.trangThai === "Đang xử lý" ? "Đang xử lý" : "",
              };
            } else {
              // Bật "Quá hạn": CHỈ được chọn cùng "Đang xử lý" (nếu Đang xử lý đang bật), các tab khác như "Đã giải quyết" bị bỏ
              next = {
                ...filters,
                tinhTrang: "OVERDUE",
                trangThai: filters.trangThai === "Đang xử lý" ? "Đang xử lý" : "",
              };
            }
            handleFilterChange(next);
          }}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${filters.tinhTrang === "OVERDUE"
              ? "border-rose-500 ring-1 ring-rose-500/30 shadow-xs bg-rose-50/20"
              : "border-gray-200 hover:border-gray-300"
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Quá hạn / Cần gia hạn</span>
              <span className="text-2xl font-bold text-rose-600 tracking-tight">{statCounts.overdue}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 6: Đóng */}
        <div
          onClick={() => {
            let next;
            if (filters.trangThai === "Đóng" || filters.trangThai === "DONG") {
              next = { ...filters, trangThai: "", tinhTrang: "" };
            } else {
              next = { ...filters, trangThai: "Đóng", tinhTrang: "" };
            }
            handleFilterChange(next);
          }}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            filters.trangThai === "Đóng" || filters.trangThai === "DONG"
              ? "border-slate-600 ring-1 ring-slate-600/30 shadow-xs bg-slate-100/60"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đóng</span>
              <span className="text-2xl font-bold text-slate-700 tracking-tight">{statCounts.closed}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center">
              <Archive className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* 4. THANH BỘ LỌC NÂNG CAO */}
      <ReportFilter
        onFilter={handleFilterChange}
        onReset={handleResetFilters}
        filters={filters}
        pagination={pagination}
        statusReport={statusReport}
        reportAreas={scopedReportAreas}
        extent={extent}
        extraActions={columnSettingComponent}
      />

      {/* 5. BẢNG DỮ LIỆU PHẢN ÁNH */}
      <div className="bg-white rounded-lg shadow-xs border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
            <p className="mt-2 text-xs md:text-sm text-gray-500">Đang tải dữ liệu phản ánh...</p>
          </div>
        ) : (
          <BaseTable
            data={reports}
            columns={filteredColumns}
            onView={handleView}
            onApprove={handleOpenApproveModal}
            canApprove={isReportPending}
            onReject={handleOpenRejectModal}
            canReject={isReportPending}
            onUpdateStatus={handleEdit}
            canUpdateStatus={(r) => !isReportPending(r) && !LOCKED_STATUSES.includes(getLatestStatusCode(r))}
            onUpdateCategory={handleOpenCategoryModal}
            canUpdateCategory={canUpdateCategoryRow}
            onAssign={handleOpenAssignModal}
            canAssign={canAssignRow}
            onExtension={handleOpenExtensionModal}
            canExtension={canExtensionRow}
            actionColumnWidth="80px"
            showActions={true}
            emptyMessage="Không có phản ánh nào"
            sortState={{ key: filters.sortBy, order: filters.sortOrder }}
            onSort={handleSort}
            pagination={{
              current: pagination.currentPage,
              pageSize: pagination.pageSize,
              total: pagination.totalItems,
              totalPages: pagination.totalPages,
              onChange: handlePageChange,
            }}
          />
        )}
      </div>

      {/* 6. TẬP HỢP CÁC MODALS THAO TÁC */}
      <ReportDetailModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setSelectedReport(null);
          setModalMode("view");
          clearError();
          if (id) {
            navigate('/reports', { replace: true });
          }
        }}
        report={selectedReport}
        loading={!selectedReport && isPreviewModalOpen}
        onOpenApprove={handleOpenApproveModal}
        onOpenReject={handleOpenRejectModal}
        onStatusUpdated={() => {
          clearError();
          loadReports({
            page: currentPage,
            size: filters.pageSize,
            trangThai: filters.trangThai,
            idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
            mucDo: filters.mucDo,
            maPhanAnh: filters.maPhanAnh,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          });
        }}
        mode={modalMode}
        onModeChange={setModalMode}
      />

      <ReportApproveModal
        isOpen={isApproveModalOpen}
        onClose={handleCloseApproveModal}
        report={approveModalReport}
        onSuccess={() => {
          loadReports({
            page: currentPage,
            size: filters.pageSize,
            trangThai: filters.trangThai,
            idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
            mucDo: filters.mucDo,
            maPhanAnh: filters.maPhanAnh,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          });
        }}
      />

      <ReportRejectModal
        isOpen={isRejectModalOpen}
        onClose={handleCloseRejectModal}
        report={rejectModalReport}
        onSuccess={() => {
          loadReports({
            page: currentPage,
            size: filters.pageSize,
            trangThai: filters.trangThai,
            idLinhVucPhanAnh: filters.idLinhVucPhanAnh,
            mucDo: filters.mucDo,
            maPhanAnh: filters.maPhanAnh,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          });
        }}
      />

      <UpdateCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        report={categoryModalReport}
        reportAreas={reportAreas}
        onSubmit={handleSubmitUpdateCategory}
        loading={updatingCategory}
      />

      <AssignStaffModal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        report={assignModalReport}
        staffOptions={staffOptions}
        loadingOptions={loadingStaffOptions}
        onSubmit={handleSubmitAssign}
        loading={assigningStaff}
      />

      <ReportCreateModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleSubmitCreateReport}
        isLoading={creatingReport}
        reportAreas={reportAreas}
        extent={extent}
      />

      <ReportExtensionModal
        isOpen={isExtensionModalOpen}
        onClose={handleCloseExtensionModal}
        report={extensionModalReport}
        onSuccess={handleRefresh}
      />
    </div>
  );
}
